import {
  lazy,
  Suspense,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Activity,
  BarChart3,
  Clock3,
  Crosshair,
  Download,
  DownloadCloud,
  History,
  Home,
  MoreVertical,
  Plus,
  Play,
  RotateCcw,
  Settings,
  ShieldCheck,
  Target,
  Trophy,
  Trash2,
  Undo2,
  Upload,
  UserRound,
  Users,
  Volume2,
  VolumeX,
  X,
} from "lucide-react";
import { playSound, preloadSounds, scoreSoundCue } from "./audio";
import { checkoutSuggestion } from "./checkout";
import {
  activePlayer,
  activeTeam,
  allThrows,
  createGame,
  endTurn,
  rematch,
  throwDart,
} from "./gameEngine";
import { matchStats, playerStats, teamStats } from "./stats";
import {
  archiveCompletedMatch,
  deleteArchivedMatch,
  exportLocalBackup,
  importLocalBackup,
  loadArchivedMatches,
  loadProfiles,
  loadTournaments,
  migrateLegacyHistory,
  saveTournament,
} from "./historyDb";
import type { ArchivedMatch, LocalProfile } from "./historyDb";
import {
  createTournament,
  nextTournamentMatch,
  recordTournamentResult,
  tournamentStandings,
} from "./tournament";
import type { Tournament, TournamentFormat, TournamentMatch } from "./tournament";
import {
  archiveGame,
  exportGame,
  loadGame,
  loadPreferences,
  loadUndoHistory,
  migrateGame,
  saveGame,
  savePreferences,
  saveUndoHistory,
} from "./storage";
import type {
  AppPreferences,
  DartInput,
  GameConfig,
  GameState,
  MatchMode,
  Multiplier,
  TeamDraft,
} from "./types";

const defaultConfig: GameConfig = {
  startScore: 501,
  legsToWin: 2,
  doubleOut: true,
  sound: true,
};
const defaultDrafts: TeamDraft[] = [
  { name: "PLAYER 1", players: ["PLAYER 1"] },
  { name: "PLAYER 2", players: ["PLAYER 2"] },
];
const savedGame = loadGame();
const Heatmap3D = lazy(() => import("./Heatmap3D"));

const draftsFromGame = (game?: GameState): TeamDraft[] =>
  game
    ? game.teams.map((team) => ({
        name: team.name,
        players: team.playerIds.map(
          (id) => game.players.find((player) => player.id === id)?.name ?? "PLAYER",
        ),
      }))
    : defaultDrafts;

function SetupModal({
  initial,
  preferences,
  onStart,
  onClose,
}: {
  initial?: GameState;
  preferences: AppPreferences;
  onStart: (
    drafts: TeamDraft[],
    mode: MatchMode,
    config: GameConfig,
    preferences: AppPreferences,
  ) => void;
  onClose?: () => void;
}) {
  const [mode, setMode] = useState<MatchMode>(initial?.mode ?? "individual");
  const [drafts, setDrafts] = useState<TeamDraft[]>(draftsFromGame(initial));
  const [startScore, setStartScore] = useState(initial?.config.startScore ?? 501);
  const [legs, setLegs] = useState(initial?.config.legsToWin ?? 2);
  const [doubleOut, setDoubleOut] = useState(initial?.config.doubleOut ?? true);
  const [sound, setSound] = useState(initial?.config.sound ?? true);
  const [soundVolume, setSoundVolume] = useState(preferences.soundVolume);

  const switchMode = (nextMode: MatchMode) => {
    if (nextMode === mode) return;
    setDrafts((current) =>
      nextMode === "individual"
        ? current.flatMap((team) =>
            team.players.map((name) => ({ name, players: [name] })),
          )
        : current.map((team, index) => ({
            name: current.length === 1 ? `SQUADRA ${index + 1}` : team.name,
            players: team.players,
          })),
    );
    setMode(nextMode);
  };

  const updateTeam = (teamIndex: number, patch: Partial<TeamDraft>) =>
    setDrafts((current) =>
      current.map((team, index) => (index === teamIndex ? { ...team, ...patch } : team)),
    );

  const updatePlayer = (teamIndex: number, playerIndex: number, value: string) =>
    setDrafts((current) =>
      current.map((team, index) =>
        index === teamIndex
          ? {
              ...team,
              name: mode === "individual" ? value : team.name,
              players: team.players.map((name, memberIndex) =>
                memberIndex === playerIndex ? value : name,
              ),
            }
          : team,
      ),
    );

  const totalPlayers = drafts.reduce((sum, team) => sum + team.players.length, 0);
  const valid =
    drafts.length >= 2 &&
    drafts.every(
      (team) =>
        team.players.length > 0 &&
        team.players.every((name) => name.trim()) &&
        (mode === "individual" || team.name.trim()),
    );

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label="Configura partita">
      <form
        className="setup-modal team-setup-modal"
        onSubmit={(event) => {
          event.preventDefault();
          if (!valid) return;
          onStart(
            drafts,
            mode,
            { startScore, legsToWin: legs, doubleOut, sound },
            { soundVolume },
          );
        }}
      >
        <div className="modal-heading">
          <div className="brand-mark small"><Target /></div>
          <div>
            <span className="eyebrow">MATCH CONTROL</span>
            <h2>NUOVA PARTITA</h2>
          </div>
          {onClose && (
            <button type="button" className="icon-button" onClick={onClose} aria-label="Chiudi">
              <X />
            </button>
          )}
        </div>

        <div className="setup-scroll-content">
          <div className="match-mode-switch" role="radiogroup" aria-label="Modalità partita">
            <button
              type="button"
              role="radio"
              aria-checked={mode === "individual"}
              className={mode === "individual" ? "selected" : ""}
              onClick={() => switchMode("individual")}
            >
              <UserRound /><span><strong>INDIVIDUALE</strong><small>Uno contro uno o tutti contro tutti</small></span>
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={mode === "teams"}
              className={mode === "teams" ? "selected" : ""}
              onClick={() => switchMode("teams")}
            >
              <Users /><span><strong>SQUADRE</strong><small>Punteggio condiviso, stats individuali</small></span>
            </button>
          </div>

          <div className="setup-section">
            <div className="section-title">
              <span>{mode === "teams" ? "SQUADRE E COMPONENTI" : "GIOCATORI"}</span>
              <small>{mode === "teams" ? `${drafts.length} TEAM · ${totalPlayers} PLAYER` : `${totalPlayers} IN GARA`}</small>
            </div>
            <div className={`team-editor ${mode}`}>
              {drafts.map((team, teamIndex) => (
                <section className="team-editor-card" key={teamIndex}>
                  {mode === "teams" && (
                    <div className="team-name-input">
                      <Users size={17} />
                      <input
                        value={team.name}
                        onChange={(event) => updateTeam(teamIndex, { name: event.target.value })}
                        aria-label={`Nome squadra ${teamIndex + 1}`}
                        maxLength={20}
                        required
                      />
                      {drafts.length > 2 && (
                        <button
                          type="button"
                          onClick={() =>
                            setDrafts((current) =>
                              current.filter((_, index) => index !== teamIndex),
                            )
                          }
                          aria-label={`Rimuovi ${team.name}`}
                        >
                          <X size={16} />
                        </button>
                      )}
                    </div>
                  )}
                  <div className="player-inputs">
                    {team.players.map((name, playerIndex) => (
                      <label className="player-input" key={playerIndex}>
                        <span
                          className="player-dot"
                          style={{ "--player-index": teamIndex + playerIndex } as React.CSSProperties}
                        />
                        <input
                          value={name}
                          onChange={(event) =>
                            updatePlayer(teamIndex, playerIndex, event.target.value)
                          }
                          aria-label={`Nome giocatore ${playerIndex + 1} ${team.name}`}
                          maxLength={18}
                          required
                        />
                        {(mode === "teams"
                          ? team.players.length > 1
                          : drafts.length > 2) && (
                          <button
                            type="button"
                            onClick={() =>
                              mode === "individual"
                                ? setDrafts((current) =>
                                    current.filter((_, index) => index !== teamIndex),
                                  )
                                : updateTeam(teamIndex, {
                                    players: team.players.filter(
                                      (_, index) => index !== playerIndex,
                                    ),
                                  })
                            }
                            aria-label={`Rimuovi ${name}`}
                          >
                            <X size={17} />
                          </button>
                        )}
                      </label>
                    ))}
                  </div>
                  {mode === "teams" && (
                    <button
                      type="button"
                      className="add-member"
                      onClick={() =>
                        updateTeam(teamIndex, {
                          players: [
                            ...team.players,
                            `PLAYER ${totalPlayers + 1}`,
                          ],
                        })
                      }
                    >
                      <Plus size={15} /> AGGIUNGI COMPONENTE
                    </button>
                  )}
                </section>
              ))}
            </div>
            <button
              type="button"
              className="add-player"
              onClick={() =>
                setDrafts((current) => [
                  ...current,
                  mode === "teams"
                    ? {
                        name: `SQUADRA ${current.length + 1}`,
                        players: [`PLAYER ${totalPlayers + 1}`],
                      }
                    : {
                        name: `PLAYER ${current.length + 1}`,
                        players: [`PLAYER ${current.length + 1}`],
                      },
                ])
              }
            >
              <Plus size={18} /> {mode === "teams" ? "AGGIUNGI SQUADRA" : "AGGIUNGI PLAYER"}
            </button>
          </div>

          <div className="setup-grid">
            <label>
              <span>PUNTEGGIO</span>
              <select value={startScore} onChange={(event) => setStartScore(Number(event.target.value))}>
                {[101, 301, 501, 701, 1001].map((score) => (
                  <option key={score} value={score}>{score}</option>
                ))}
              </select>
            </label>
            <label>
              <span>LEGS PER VINCERE</span>
              <select value={legs} onChange={(event) => setLegs(Number(event.target.value))}>
                {[1, 2, 3, 4, 5, 7, 9].map((value) => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </select>
            </label>
          </div>

          <div className="toggle-list">
            <button
              type="button"
              className={doubleOut ? "selected" : ""}
              onClick={() => setDoubleOut(!doubleOut)}
            >
              <ShieldCheck />
              <span><strong>DOUBLE OUT</strong><small>Chiudi obbligatoriamente con un doppio</small></span>
              <i>{doubleOut ? "ON" : "OFF"}</i>
            </button>
            <button
              type="button"
              className={sound ? "selected" : ""}
              onClick={() => setSound(!sound)}
            >
              {sound ? <Volume2 /> : <VolumeX />}
              <span><strong>SUONI MP3</strong><small>Tiro, cambio player e annulla</small></span>
              <i>{sound ? "ON" : "OFF"}</i>
            </button>
            <label className="volume-control">
              <Volume2 />
              <span><strong>VOLUME</strong><small>{Math.round(soundVolume * 100)}%</small></span>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={soundVolume}
                onChange={(event) => setSoundVolume(Number(event.target.value))}
                aria-label="Volume suoni"
              />
            </label>
          </div>
        </div>

        <button className="primary-cta" type="submit" disabled={!valid}>
          <span>INIZIA LA PARTITA</span>
          <Target />
        </button>
      </form>
    </div>
  );
}

function PlayerCard({ game, playerId }: { game: GameState; playerId: string }) {
  const player = game.players.find((item) => item.id === playerId)!;
  const team = game.teams.find((item) => item.id === player.teamId)!;
  const stats = playerStats(game, player.id);
  const isActive = game.activePlayerId === player.id;
  const dartsLeft = isActive ? 3 - game.currentTurn.length : 3;
  const checkout = isActive
    ? checkoutSuggestion(team.score, dartsLeft, game.config.doubleOut)
    : [];
  const lastTurn = [...game.turns].reverse().find((turn) => turn.playerId === player.id);

  return (
    <article
      className={`player-card ${isActive ? "active" : ""}`}
      data-player-id={player.id}
      aria-current={isActive ? "true" : undefined}
      style={{ "--player-color": team.color } as React.CSSProperties}
    >
      <div className="player-card-top">
        <span className="player-name">{player.name}</span>
        {game.mode === "teams" && <span className="team-chip">{team.name}</span>}
        <span className="leg-badge">{team.legs}<small>LEGS</small></span>
      </div>
      <div className="score-row">
        <strong>{team.score}</strong>
        <div className="darts-left" aria-label={`${dartsLeft} freccette disponibili`}>
          {[0, 1, 2].map((dart) => (
            <span key={dart} className={dart < dartsLeft ? "ready" : "used"}>➤</span>
          ))}
        </div>
      </div>
      <div className="player-meta">
        <span><small>AVG 3</small><strong>{stats.average.toFixed(1)}</strong></span>
        <span><small>ULTIMO</small><strong>{lastTurn ? (lastTurn.bust ? "BUST" : lastTurn.score) : "—"}</strong></span>
        <span className="checkout-hint">
          <small>{checkout.length ? "CHECKOUT" : isActive ? "IN PEDANA" : "IN ATTESA"}</small>
          <strong>{checkout.length ? checkout.join(" · ") : isActive ? `${dartsLeft} DART` : "—"}</strong>
        </span>
      </div>
    </article>
  );
}

function PlayerRail({ game }: { game: GameState }) {
  const railRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const rail = railRef.current;
    if (!rail) return;
    let frame = 0;
    const centerActive = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const card = rail.querySelector<HTMLElement>(
          `[data-player-id="${CSS.escape(game.activePlayerId)}"]`,
        );
        if (!card) return;
        const sideSpace = Math.max(10, (rail.clientWidth - card.offsetWidth) / 2);
        rail.style.paddingLeft = `${sideSpace}px`;
        rail.style.paddingRight = `${sideSpace}px`;
        rail.scrollTo({
          left: card.offsetLeft - (rail.clientWidth - card.offsetWidth) / 2,
          behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
            ? "auto"
            : "smooth",
        });
      });
    };
    centerActive();
    const observer = new ResizeObserver(centerActive);
    observer.observe(rail);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [game.activePlayerId]);

  return (
    <section className="players-strip" aria-label="Giocatori" ref={railRef}>
      {game.teams.flatMap((team) =>
        team.playerIds.map((playerId) => (
          <PlayerCard key={playerId} game={game} playerId={playerId} />
        )),
      )}
    </section>
  );
}

function ScoreSection({
  title,
  multiplier,
  onScore,
}: {
  title: string;
  multiplier: Multiplier;
  onScore: (dart: DartInput) => void;
}) {
  const values = Array.from({ length: 20 }, (_, index) => 20 - index);
  const prefix = multiplier === 1 ? "S" : multiplier === 2 ? "D" : "T";
  return (
    <section className={`score-section ${title.toLowerCase()}`}>
      <div className="score-section-label"><span>{title}</span></div>
      <div className="score-grid">
        {values.map((number) => (
          <button
            key={number}
            onClick={() => onScore({ segment: number, multiplier, label: `${prefix}${number}` })}
            aria-label={`${title} ${number}`}
          >
            <strong>{number}</strong>
            <span>{Array.from({ length: multiplier }, () => "•").join("")}</span>
          </button>
        ))}
      </div>
    </section>
  );
}

const percentage = (value: number) => `${Math.round(value)}%`;
const formatDuration = (milliseconds: number) => {
  const totalMinutes = Math.floor(milliseconds / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return hours ? `${hours}h ${minutes}m` : `${Math.max(1, minutes)} min`;
};

function PlayerAnalysis({ game, playerId }: { game: GameState; playerId: string }) {
  const player = game.players.find((item) => item.id === playerId)!;
  const team = game.teams.find((item) => item.id === player.teamId)!;
  const stats = playerStats(game, playerId);
  const bands = ["<40", "40+", "60+", "80+", "100+", "140+", "180"];
  const maxBand = Math.max(1, ...stats.scoreBands);
  const metric = (label: string, value: string | number) => (
    <div><dt>{label}</dt><dd>{value}</dd></div>
  );

  return (
    <section className="player-deep-dive" style={{ "--player-color": team.color } as React.CSSProperties}>
      <div className="stats-hero">
        <div>
          <span className="eyebrow">{game.mode === "teams" ? team.name : "MEDIA 3 FRECCE"}</span>
          <strong>{stats.average.toFixed(1)}</strong>
          <small>{stats.totalPoints} punti in {stats.darts} freccette</small>
        </div>
        <div className="consistency-ring" style={{ "--consistency": Math.min(100, stats.consistency) } as React.CSSProperties}>
          <strong>{stats.consistency.toFixed(1)}</strong><small>DEVIAZIONE</small>
        </div>
      </div>
      <div className="stats-groups">
        <article><h3>RENDIMENTO</h3><dl>
          {metric("First 9", stats.firstNine.toFixed(1))}
          {metric("Media turno", stats.averagePerTurn.toFixed(1))}
          {metric("Turno massimo", stats.highestTurn)}
          {metric("Turno minimo", stats.lowestTurn)}
          {metric("Mediana", stats.medianTurn.toFixed(1))}
          {metric("Turni", stats.turns)}
          {metric("Punti", stats.totalPoints)}
          {metric("Bust", stats.busts)}
        </dl></article>
        <article><h3>CHIUSURE</h3><dl>
          {metric("Checkout", stats.checkouts)}
          {metric("Opportunità", stats.checkoutAttempts)}
          {metric("Checkout %", percentage(stats.checkoutRate))}
          {metric("Checkout max", stats.checkoutHigh || "—")}
          {metric("Doppi registrati", stats.doublesAttempted)}
          {metric("Doppi chiusura", stats.doublesHit)}
          {metric("Double %", percentage(stats.doubleRate))}
          {metric("Darts / leg chiuso", stats.averageDartsPerWonLeg ? stats.averageDartsPerWonLeg.toFixed(1) : "—")}
        </dl></article>
        <article><h3>TURNI ALTI</h3><dl>
          {metric("60–79", stats.sixties)}
          {metric("80–99", stats.eighties)}
          {metric("100–119", stats.hundreds)}
          {metric("120–139", stats.oneTwenties)}
          {metric("140–159", stats.oneForties)}
          {metric("160–179", stats.oneSixties)}
          {metric("180", stats.oneEighties)}
          {metric("Checkout eseguiti", stats.checkouts)}
        </dl></article>
        <article><h3>PRECISIONE</h3><dl>
          {metric("Single", stats.hitBreakdown.singles)}
          {metric("Double", stats.hitBreakdown.doubles)}
          {metric("Triple", stats.hitBreakdown.triples)}
          {metric("Bull 25", stats.hitBreakdown.bull25)}
          {metric("Bull 50", stats.hitBreakdown.bull50)}
          {metric("Miss", stats.hitBreakdown.misses)}
        </dl></article>
      </div>
      <div className="stats-charts">
        <article><h3>DISTRIBUZIONE TURNI</h3>
          <div className="distribution-chart">
            {stats.scoreBands.map((count, index) => (
              <div key={bands[index]}>
                <i style={{ height: `${Math.max(4, (count / maxBand) * 100)}%` }} />
                <strong>{count}</strong><small>{bands[index]}</small>
              </div>
            ))}
          </div>
        </article>
        <article><h3>ULTIMI 12 TURNI</h3>
          <div className="turn-chart">
            {stats.turnScores.slice(-12).map((score, index) => (
              <i key={index} style={{ height: `${Math.max(4, (score / 180) * 100)}%` }}>
                <span>{score}</span>
              </i>
            ))}
            {!stats.turnScores.length && <small>Nessun turno completato</small>}
          </div>
        </article>
      </div>
    </section>
  );
}

function AnalyticsContent({ game, final = false }: { game: GameState; final?: boolean }) {
  const [view, setView] = useState<"match" | "teams" | "players">("match");
  const [selectedPlayerId, setSelectedPlayerId] = useState(game.activePlayerId);
  const [selectedTeamId, setSelectedTeamId] = useState(
    game.winnerTeamId ?? game.teams[game.activeTeam]?.id ?? game.teams[0].id,
  );
  const summary = matchStats(game);
  const averageLeader = game.players.find((player) => player.id === summary.averageLeaderId);
  const checkoutLeader = game.players.find((player) => player.id === summary.checkoutLeaderId);

  return (
    <>
      <div className="analytics-view-tabs" role="tablist" aria-label="Tipo di analisi">
        <button className={view === "match" ? "selected" : ""} onClick={() => setView("match")}>MATCH</button>
        {game.mode === "teams" && (
          <button className={view === "teams" ? "selected" : ""} onClick={() => setView("teams")}>SQUADRE</button>
        )}
        <button className={view === "players" ? "selected" : ""} onClick={() => setView("players")}>GIOCATORI</button>
      </div>

      {view === "match" && (
        <>
          <section className="match-overview">
            <div><Clock3 /><span><small>DURATA</small><strong>{formatDuration(summary.durationMs)}</strong></span></div>
            <div><Target /><span><small>FRECCETTE</small><strong>{summary.totalDarts}</strong></span></div>
            <div><Activity /><span><small>MEDIA MATCH</small><strong>{summary.overallAverage.toFixed(1)}</strong></span></div>
            <div><BarChart3 /><span><small>TURNI</small><strong>{summary.totalTurns}</strong></span></div>
            <div><Trophy /><span><small>LEADER AVG</small><strong>{averageLeader?.name ?? "—"}</strong></span></div>
            <div><Crosshair /><span><small>CHECKOUT MAX</small><strong>{checkoutLeader?.name ?? "—"}</strong></span></div>
            <div><Activity /><span><small>BUST</small><strong>{summary.totalBusts}</strong></span></div>
            <div><Target /><span><small>180</small><strong>{summary.total180s}</strong></span></div>
          </section>

          {game.mode === "teams" && (
            <section className="team-comparison-grid">
              {game.teams.map((team) => {
                const stats = teamStats(game, team.id);
                return (
                  <article key={team.id} style={{ "--team-color": team.color } as React.CSSProperties}>
                    <span>{final && team.id === game.winnerTeamId ? "VINCITRICE" : "SQUADRA"}</span>
                    <h3>{team.name}</h3>
                    <strong>{team.legs} LEGS</strong>
                    <dl>
                      <div><dt>AVG</dt><dd>{stats.average.toFixed(1)}</dd></div>
                      <div><dt>FIRST 9</dt><dd>{stats.firstNine.toFixed(1)}</dd></div>
                      <div><dt>CO%</dt><dd>{percentage(stats.checkoutRate)}</dd></div>
                      <div><dt>MAX</dt><dd>{stats.highestTurn}</dd></div>
                    </dl>
                  </article>
                );
              })}
            </section>
          )}

          <section className="player-comparison-table">
            <h3>CONFRONTO PLAYER-BY-PLAYER</h3>
            <div className="comparison-row comparison-head">
              <span>PLAYER</span><span>TEAM</span><span>AVG</span><span>FIRST 9</span><span>MAX</span><span>CO%</span><span>180</span>
            </div>
            {game.players.map((player) => {
              const stats = playerStats(game, player.id);
              const team = game.teams.find((item) => item.id === player.teamId)!;
              return (
                <div className="comparison-row" key={player.id}>
                  <strong style={{ color: team.color }}>{player.name}</strong>
                  <span>{game.mode === "teams" ? team.name : "—"}</span>
                  <span>{stats.average.toFixed(1)}</span>
                  <span>{stats.firstNine.toFixed(1)}</span>
                  <span>{stats.highestTurn}</span>
                  <span>{percentage(stats.checkoutRate)}</span>
                  <span>{stats.oneEighties}</span>
                </div>
              );
            })}
          </section>
        </>
      )}

      {view === "teams" && game.mode === "teams" && (
        <>
          <div className="stats-player-tabs" role="tablist" aria-label="Scegli squadra">
            {game.teams.map((team) => (
              <button
                key={team.id}
                className={selectedTeamId === team.id ? "selected" : ""}
                onClick={() => setSelectedTeamId(team.id)}
                style={{ "--player-color": team.color } as React.CSSProperties}
              >
                <span /><strong>{team.name}</strong><small>{team.legs} LEGS</small>
              </button>
            ))}
          </div>
          {game.teams.filter((team) => team.id === selectedTeamId).map((team) => {
            const stats = teamStats(game, team.id);
            return (
              <section className="team-deep-dive" key={team.id}>
                <div className="stats-hero">
                  <div><span className="eyebrow">MEDIA SQUADRA</span><strong>{stats.average.toFixed(1)}</strong><small>{stats.totalPoints} punti · {stats.darts} freccette</small></div>
                  <div><strong>{team.legs}</strong><small>LEGS</small></div>
                </div>
                <div className="team-stat-grid">
                  <div><small>FIRST 9</small><strong>{stats.firstNine.toFixed(1)}</strong></div>
                  <div><small>TURNO MAX</small><strong>{stats.highestTurn}</strong></div>
                  <div><small>CHECKOUT</small><strong>{stats.checkouts}</strong></div>
                  <div><small>CHECKOUT %</small><strong>{percentage(stats.checkoutRate)}</strong></div>
                  <div><small>CHECKOUT MAX</small><strong>{stats.checkoutHigh || "—"}</strong></div>
                  <div><small>BUST</small><strong>{stats.busts}</strong></div>
                  <div><small>100+</small><strong>{stats.hundredsPlus}</strong></div>
                  <div><small>140+</small><strong>{stats.oneFortiesPlus}</strong></div>
                  <div><small>180</small><strong>{stats.oneEighties}</strong></div>
                </div>
                <div className="member-contributions">
                  <h3>CONTRIBUTO COMPONENTI</h3>
                  {stats.contributions.map((contribution) => {
                    const player = game.players.find((item) => item.id === contribution.playerId)!;
                    return (
                      <div key={player.id}>
                        <strong>{player.name}</strong>
                        <span>Punti {percentage(contribution.pointsPercent)}</span>
                        <span>Freccette {percentage(contribution.dartsPercent)}</span>
                        <span>Checkout {percentage(contribution.checkoutPercent)}</span>
                      </div>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </>
      )}

      {view === "players" && (
        <>
          <div className="stats-player-tabs" role="tablist" aria-label="Scegli giocatore">
            {game.players.map((player) => {
              const team = game.teams.find((item) => item.id === player.teamId)!;
              const stats = playerStats(game, player.id);
              return (
                <button
                  key={player.id}
                  className={selectedPlayerId === player.id ? "selected" : ""}
                  onClick={() => setSelectedPlayerId(player.id)}
                  style={{ "--player-color": team.color } as React.CSSProperties}
                >
                  <span /><strong>{player.name}</strong><small>{stats.average.toFixed(1)} AVG</small>
                </button>
              );
            })}
          </div>
          <PlayerAnalysis game={game} playerId={selectedPlayerId} />
        </>
      )}
    </>
  );
}

function StatsModal({ game, onClose }: { game: GameState; onClose: () => void }) {
  return (
    <div className="modal-backdrop stats-backdrop" role="dialog" aria-modal="true" aria-label="Statistiche match">
      <div className="stats-modal">
        <div className="modal-heading">
          <BarChart3 />
          <div><span className="eyebrow">ANALISI COMPLETA</span><h2>STATISTICHE MATCH</h2></div>
          <button className="icon-button" onClick={onClose} aria-label="Chiudi statistiche"><X /></button>
        </div>
        <div className="stats-scroll-content"><AnalyticsContent game={game} /></div>
      </div>
    </div>
  );
}

function HistoryModal({ onClose }: { onClose: () => void }) {
  const [matches, setMatches] = useState<ArchivedMatch[]>([]);
  const [profiles, setProfiles] = useState<LocalProfile[]>([]);
  const [view, setView] = useState<"matches" | "players" | "head">("matches");
  const [query, setQuery] = useState("");
  const [selectedMatch, setSelectedMatch] = useState<ArchivedMatch | null>(null);
  const [selectedProfile, setSelectedProfile] = useState<string>("");
  const [compareA, setCompareA] = useState("");
  const [compareB, setCompareB] = useState("");
  const [heatmapOpen, setHeatmapOpen] = useState(false);

  const refresh = async () => {
    const [nextMatches, nextProfiles] = await Promise.all([loadArchivedMatches(), loadProfiles()]);
    setMatches(nextMatches);
    setProfiles(nextProfiles);
    if (!selectedProfile && nextProfiles[0]) setSelectedProfile(nextProfiles[0].id);
    if (!compareA && nextProfiles[0]) setCompareA(nextProfiles[0].id);
    if (!compareB && nextProfiles[1]) setCompareB(nextProfiles[1].id);
  };

  useEffect(() => { void refresh(); }, []);

  const profileSummary = (profileId: string) => {
    let darts = 0;
    let points = 0;
    let wins = 0;
    let legs = 0;
    let checkoutHigh = 0;
    let oneEighties = 0;
    const relevant = matches.filter((match) => Object.values(match.profileIds).includes(profileId));
    relevant.forEach((match) => {
      const playerId = Object.entries(match.profileIds).find(([, id]) => id === profileId)?.[0];
      if (!playerId) return;
      const stats = playerStats(match.game, playerId);
      const player = match.game.players.find((item) => item.id === playerId);
      const team = match.game.teams.find((item) => item.id === player?.teamId);
      darts += stats.darts;
      points += stats.totalPoints;
      legs += team?.legs ?? 0;
      checkoutHigh = Math.max(checkoutHigh, stats.checkoutHigh);
      oneEighties += stats.oneEighties;
      if (team?.id === match.game.winnerTeamId) wins += 1;
    });
    return {
      matches: relevant.length,
      wins,
      legs,
      average: darts ? (points / darts) * 3 : 0,
      checkoutHigh,
      oneEighties,
    };
  };

  const visibleMatches = matches.filter((match) =>
    !query.trim() ||
    match.game.players.some((player) => player.name.toLowerCase().includes(query.toLowerCase())) ||
    match.game.teams.some((team) => team.name.toLowerCase().includes(query.toLowerCase())),
  );
  const headMatches = matches.filter((match) =>
    compareA && compareB &&
    Object.values(match.profileIds).includes(compareA) &&
    Object.values(match.profileIds).includes(compareB),
  );

  return (
    <div className="modal-backdrop stats-backdrop" role="dialog" aria-modal="true" aria-label="Storico locale">
      <div className="stats-modal history-modal">
        <div className="modal-heading">
          <History />
          <div><span className="eyebrow">SALVATO SUL DISPOSITIVO</span><h2>STORICO</h2></div>
          <button className="icon-button" onClick={onClose} aria-label="Chiudi storico"><X /></button>
        </div>
        <div className="stats-scroll-content">
          {selectedMatch ? (
            <>
              <div className="history-detail-heading">
                <button onClick={() => setSelectedMatch(null)}>← INDIETRO</button>
                <strong>{new Date(selectedMatch.archivedAt).toLocaleString("it-IT")}</strong>
                <button onClick={() => setHeatmapOpen(true)}><Crosshair /> BERSAGLIO 3D</button>
              </div>
              <AnalyticsContent game={selectedMatch.game} final />
              {heatmapOpen && (
                <Suspense fallback={<div className="loading-panel">Caricamento bersaglio 3D…</div>}>
                  <Heatmap3D
                    darts={allThrows(selectedMatch.game)}
                    players={selectedMatch.game.players}
                    teams={selectedMatch.game.teams}
                    mode={selectedMatch.game.mode}
                    onClose={() => setHeatmapOpen(false)}
                  />
                </Suspense>
              )}
            </>
          ) : (
            <>
              <div className="analytics-view-tabs">
                <button className={view === "matches" ? "selected" : ""} onClick={() => setView("matches")}>PARTITE</button>
                <button className={view === "players" ? "selected" : ""} onClick={() => setView("players")}>GIOCATORI</button>
                <button className={view === "head" ? "selected" : ""} onClick={() => setView("head")}>TESTA A TESTA</button>
              </div>
              {view === "matches" && (
                <>
                  <input className="history-search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Cerca giocatore o squadra…" />
                  <div className="history-match-list">
                    {visibleMatches.map((match) => {
                      const winner = match.game.teams.find((team) => team.id === match.game.winnerTeamId);
                      return (
                        <article key={match.id}>
                          <button onClick={() => setSelectedMatch(match)}>
                            <small>{new Date(match.archivedAt).toLocaleString("it-IT")}</small>
                            <strong>{winner?.name ?? "Partita"}</strong>
                            <span>{match.game.players.map((player) => player.name).join(" · ")} · {match.game.config.startScore} · {match.game.mode === "teams" ? "Squadre" : "Individuale"}</span>
                          </button>
                          <button aria-label="Elimina partita" onClick={() => {
                            if (window.confirm("Eliminare questa partita dallo storico?")) {
                              void deleteArchivedMatch(match.id).then(refresh);
                            }
                          }}><Trash2 /></button>
                        </article>
                      );
                    })}
                    {!visibleMatches.length && <div className="empty-history">Nessuna partita trovata.</div>}
                  </div>
                </>
              )}
              {view === "players" && (
                <>
                  <div className="stats-player-tabs">
                    {profiles.map((profile) => (
                      <button key={profile.id} className={selectedProfile === profile.id ? "selected" : ""} onClick={() => setSelectedProfile(profile.id)} style={{ "--player-color": profile.color } as React.CSSProperties}>
                        <span /><strong>{profile.name}</strong><small>{profileSummary(profile.id).matches} MATCH</small>
                      </button>
                    ))}
                  </div>
                  {profiles.filter((profile) => profile.id === selectedProfile).map((profile) => {
                    const stats = profileSummary(profile.id);
                    return (
                      <section className="profile-summary" key={profile.id}>
                        <h3>{profile.name}</h3>
                        <div className="team-stat-grid">
                          <div><small>MATCH</small><strong>{stats.matches}</strong></div>
                          <div><small>VITTORIE</small><strong>{stats.wins}</strong></div>
                          <div><small>WIN RATE</small><strong>{percentage(stats.matches ? stats.wins / stats.matches : 0)}</strong></div>
                          <div><small>MEDIA</small><strong>{stats.average.toFixed(1)}</strong></div>
                          <div><small>LEGS</small><strong>{stats.legs}</strong></div>
                          <div><small>CHECKOUT MAX</small><strong>{stats.checkoutHigh || "—"}</strong></div>
                          <div><small>180</small><strong>{stats.oneEighties}</strong></div>
                        </div>
                      </section>
                    );
                  })}
                </>
              )}
              {view === "head" && (
                <section className="head-to-head">
                  <div>
                    <select value={compareA} onChange={(event) => setCompareA(event.target.value)}>{profiles.map((profile) => <option value={profile.id} key={profile.id}>{profile.name}</option>)}</select>
                    <strong>VS</strong>
                    <select value={compareB} onChange={(event) => setCompareB(event.target.value)}>{profiles.map((profile) => <option value={profile.id} key={profile.id}>{profile.name}</option>)}</select>
                  </div>
                  <h3>{headMatches.length} SCONTRI DIRETTI</h3>
                  <div className="team-stat-grid">
                    {[compareA, compareB].map((id) => {
                      const profile = profiles.find((item) => item.id === id);
                      const summary = profileSummary(id);
                      const wins = headMatches.filter((match) => {
                        const playerId = Object.entries(match.profileIds).find(([, value]) => value === id)?.[0];
                        const teamId = match.game.players.find((player) => player.id === playerId)?.teamId;
                        return teamId === match.game.winnerTeamId;
                      }).length;
                      return <div key={id}><small>{profile?.name}</small><strong>{wins} W · {summary.average.toFixed(1)} AVG</strong></div>;
                    })}
                  </div>
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function TournamentModal({
  onClose,
  onLaunch,
}: {
  onClose: () => void;
  onLaunch: (tournament: Tournament, match: TournamentMatch) => void;
}) {
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("SERATA FRECCETTE");
  const [format, setFormat] = useState<TournamentFormat>("knockout");
  const [mode, setMode] = useState<MatchMode>("individual");
  const [participantsText, setParticipantsText] = useState("PLAYER 1\nPLAYER 2\nPLAYER 3\nPLAYER 4");
  const [startScore, setStartScore] = useState(501);
  const [legs, setLegs] = useState(2);
  const [doubleOut, setDoubleOut] = useState(true);
  const [randomize, setRandomize] = useState(true);

  const refresh = async () => {
    const values = await loadTournaments();
    setTournaments(values);
    if (!selectedId && values[0]) setSelectedId(values[0].id);
  };
  useEffect(() => { void refresh(); }, []);

  const parseDrafts = (): TeamDraft[] =>
    participantsText.split("\n").map((line) => line.trim()).filter(Boolean).map((line) => {
      if (mode === "individual") return { name: line, players: [line] };
      const [teamName, roster = ""] = line.split(":");
      return {
        name: teamName.trim(),
        players: roster.split(",").map((player) => player.trim()).filter(Boolean),
      };
    }).filter((draft) => draft.name && draft.players.length);

  const submit = async () => {
    const drafts = parseDrafts();
    if (drafts.length < 2) {
      window.alert("Inserisci almeno due partecipanti validi.");
      return;
    }
    const tournament = createTournament({
      name,
      format,
      mode,
      config: { startScore, legsToWin: legs, doubleOut, sound: true },
      drafts,
      randomize,
    });
    await saveTournament(tournament);
    await refresh();
    setSelectedId(tournament.id);
    setCreating(false);
  };

  const selected = tournaments.find((tournament) => tournament.id === selectedId);
  const standings = selected ? tournamentStandings(selected) : [];
  const champion = selected?.status === "finished"
    ? selected.format === "round-robin"
      ? standings[0]?.participant
      : selected.participants.find((participant) => selected.matches.at(-1)?.winnerId === participant.id)
    : undefined;

  return (
    <div className="modal-backdrop stats-backdrop" role="dialog" aria-modal="true" aria-label="Tornei">
      <div className="stats-modal tournament-modal">
        <div className="modal-heading">
          <Trophy />
          <div><span className="eyebrow">SERATE CON AMICI</span><h2>TORNEI</h2></div>
          <button className="icon-button" onClick={onClose} aria-label="Chiudi tornei"><X /></button>
        </div>
        <div className="stats-scroll-content">
          {creating ? (
            <section className="tournament-setup">
              <input value={name} onChange={(event) => setName(event.target.value)} aria-label="Nome torneo" />
              <div className="match-mode-switch">
                <button className={format === "knockout" ? "selected" : ""} onClick={() => setFormat("knockout")}><Trophy /><strong>ELIMINAZIONE</strong><small>Tabellone diretto</small></button>
                <button className={format === "round-robin" ? "selected" : ""} onClick={() => setFormat("round-robin")}><Users /><strong>GIRONE</strong><small>Tutti contro tutti</small></button>
              </div>
              <div className="match-mode-switch">
                <button className={mode === "individual" ? "selected" : ""} onClick={() => setMode("individual")}><UserRound /><strong>INDIVIDUALE</strong></button>
                <button className={mode === "teams" ? "selected" : ""} onClick={() => {
                  setMode("teams");
                  setParticipantsText("TEAM GOLD: PLAYER 1, PLAYER 2\nTEAM RED: PLAYER 3, PLAYER 4");
                }}><Users /><strong>SQUADRE</strong></button>
              </div>
              <label>PARTECIPANTI
                <textarea value={participantsText} onChange={(event) => setParticipantsText(event.target.value)} />
                <small>{mode === "teams" ? "Una squadra per riga: Nome squadra: Player 1, Player 2" : "Un giocatore per riga"}</small>
              </label>
              <div className="tournament-rules">
                <label>PUNTI<select value={startScore} onChange={(event) => setStartScore(Number(event.target.value))}>{[101,301,501,701,1001].map((value) => <option key={value}>{value}</option>)}</select></label>
                <label>LEGS<select value={legs} onChange={(event) => setLegs(Number(event.target.value))}>{[1,2,3,4,5,7,9].map((value) => <option key={value}>{value}</option>)}</select></label>
                <button className={doubleOut ? "selected" : ""} onClick={() => setDoubleOut(!doubleOut)}>DOUBLE OUT {doubleOut ? "ON" : "OFF"}</button>
                <button className={randomize ? "selected" : ""} onClick={() => setRandomize(!randomize)}>SORTEGGIO {randomize ? "CASUALE" : "MANUALE"}</button>
              </div>
              <div className="winner-actions">
                <button onClick={() => setCreating(false)}>ANNULLA</button>
                <button className="gold" onClick={() => void submit()}>CREA TORNEO</button>
              </div>
            </section>
          ) : (
            <>
              <div className="tournament-toolbar">
                <select value={selectedId} onChange={(event) => setSelectedId(event.target.value)}>
                  <option value="">Seleziona torneo</option>
                  {tournaments.map((tournament) => <option key={tournament.id} value={tournament.id}>{tournament.name}</option>)}
                </select>
                <button onClick={() => setCreating(true)}><Plus /> NUOVO TORNEO</button>
              </div>
              {selected ? (
                <section className="tournament-board">
                  <div className="tournament-title">
                    <div><span className="eyebrow">{selected.format === "knockout" ? "ELIMINAZIONE DIRETTA" : "GIRONE ALL’ITALIANA"}</span><h3>{selected.name}</h3></div>
                    <strong>{selected.status === "finished" ? "COMPLETATO" : "IN CORSO"}</strong>
                  </div>
                  {champion && <div className="tournament-champion"><Trophy /><span>CAMPIONE</span><strong>{champion.name}</strong></div>}
                  {selected.format === "round-robin" && (
                    <div className="standings-table">
                      <div><b>#</b><b>PARTECIPANTE</b><b>G</b><b>V</b><b>S</b><b>+/-</b></div>
                      {standings.map((row, index) => <div key={row.participant.id}><span>{index+1}</span><strong>{row.participant.name}</strong><span>{row.played}</span><span>{row.wins}</span><span>{row.losses}</span><span>{row.difference}</span></div>)}
                    </div>
                  )}
                  <div className="tournament-rounds">
                    {[...new Set(selected.matches.map((match) => match.round))].map((round) => (
                      <section key={round}>
                        <h4>{selected.format === "knockout" ? `ROUND ${round}` : `GIORNATA ${round}`}</h4>
                        {selected.matches.filter((match) => match.round === round).map((match) => {
                          const first = selected.participants.find((item) => item.id === match.participantAId);
                          const second = selected.participants.find((item) => item.id === match.participantBId);
                          return (
                            <article key={match.id}>
                              <span className={match.winnerId === first?.id ? "winner" : ""}>{first?.name}</span>
                              <b>{match.status === "finished" ? `${match.legsA ?? "BYE"} — ${match.legsB ?? ""}` : "VS"}</b>
                              <span className={match.winnerId === second?.id ? "winner" : ""}>{second?.name ?? "BYE"}</span>
                              {match.status === "pending" && second && <button onClick={() => onLaunch(selected, match)}>GIOCA</button>}
                            </article>
                          );
                        })}
                      </section>
                    ))}
                  </div>
                  {nextTournamentMatch(selected) && <button className="next-tournament-match" onClick={() => onLaunch(selected, nextTournamentMatch(selected)!)}>GIOCA PROSSIMO INCONTRO</button>}
                </section>
              ) : <div className="empty-history">Crea il primo torneo della serata.</div>}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function WinnerModal({
  game,
  onRematch,
  onNew,
  onTournament,
}: {
  game: GameState;
  onRematch: () => void;
  onNew: () => void;
  onTournament?: () => void;
}) {
  const winner = game.teams.find((team) => team.id === game.winnerTeamId)!;
  const checkoutPlayer = game.players.find((player) => player.id === game.checkoutPlayerId);
  const winnerStats = teamStats(game, winner.id);
  const [heatmapOpen, setHeatmapOpen] = useState(false);
  const matchDarts = useMemo(() => allThrows(game), [game]);

  return (
    <div className="modal-backdrop winner-backdrop" role="dialog" aria-modal="true" aria-label="Risultato e analisi match">
      <div className="winner-modal winner-dashboard">
        <div className="winner-scroll-content">
          <section className="winner-hero" style={{ "--team-color": winner.color } as React.CSSProperties}>
            <div className="trophy-halo"><Trophy /></div>
            <span className="eyebrow">MATCH COMPLETATO</span>
            <h2>{winner.name}</h2>
            <p>
              vince {winner.legs} legs · media {winnerStats.average.toFixed(1)}
              {checkoutPlayer ? ` · checkout decisivo di ${checkoutPlayer.name}` : ""}
            </p>
          </section>
          <section className="winner-3d-analysis">
            <div>
              <span className="eyebrow">MAPPA DI TIRO COMPLETA</span>
              <h3>ANALISI BERSAGLIO 3D</h3>
              <p>Esplora tutte le freccette e confronta squadre e giocatori sul bersaglio.</p>
            </div>
            <button
              className="gold"
              onClick={() => setHeatmapOpen(true)}
              aria-label="Apri analisi bersaglio 3D del match"
            >
              <Crosshair /> APRI BERSAGLIO 3D
            </button>
          </section>
          <AnalyticsContent game={game} final />
          <div className="winner-actions">
            {onTournament && <button onClick={onTournament}><Trophy /> TORNA AL TORNEO</button>}
            <button onClick={onRematch}><RotateCcw /> RIVINCITA</button>
            <button className="gold" onClick={onNew}><Plus /> NUOVA PARTITA</button>
          </div>
        </div>
        {heatmapOpen && (
          <Suspense fallback={<div className="modal-backdrop heatmap-backdrop"><div className="loading-panel">Caricamento bersaglio 3D…</div></div>}>
            <Heatmap3D
              darts={matchDarts}
              players={game.players}
              teams={game.teams}
              mode={game.mode}
              onClose={() => setHeatmapOpen(false)}
            />
          </Suspense>
        )}
      </div>
    </div>
  );
}

interface ActiveTournamentLink {
  tournamentId: string;
  matchId: string;
  participantIds: [string, string];
  gameCreatedAt: number;
  recorded?: boolean;
}

function Lobby({
  game,
  canResume,
  soundEnabled,
  onResume,
  onNew,
  onTournaments,
  onHistory,
  onImport,
  onBackup,
  onInstall,
  onToggleSound,
}: {
  game: GameState;
  canResume: boolean;
  soundEnabled: boolean;
  onResume: () => void;
  onNew: () => void;
  onTournaments: () => void;
  onHistory: () => void;
  onImport: () => void;
  onBackup: () => void;
  onInstall: () => void;
  onToggleSound: () => void;
}) {
  return (
    <section className="lobby-view" aria-label="Lobby Bullseye">
      <div className="lobby-scroll">
        <header className="lobby-hero">
          <div className="brand-mark"><Target /></div>
          <span className="eyebrow">DARTS SCOREKEEPER OFFLINE</span>
          <h1>BULLSEYE</h1>
          <p>Partite, tornei e statistiche salvati sul tuo dispositivo.</p>
        </header>

        {canResume && (
          <button className="lobby-resume" onClick={onResume}>
            <Play />
            <span><small>PARTITA IN CORSO</small><strong>RIPRENDI PARTITA</strong>
              <em>{game.players.map((player) => player.name).join(" · ")} · Leg {game.leg}</em>
            </span>
            <i>›</i>
          </button>
        )}

        <div className="lobby-actions">
          <button className="primary" onClick={onNew}><Plus /><span><strong>NUOVA PARTITA</strong><small>Individuale o squadre</small></span></button>
          <button onClick={onTournaments}><Trophy /><span><strong>TORNEI</strong><small>Eliminazione o girone</small></span></button>
          <button onClick={onHistory}><History /><span><strong>STORICO E PROFILI</strong><small>Partite e confronti</small></span></button>
          <button onClick={onImport}><Upload /><span><strong>IMPORTA</strong><small>Partita o backup JSON</small></span></button>
          <button onClick={onBackup}><Download /><span><strong>BACKUP COMPLETO</strong><small>Salva tutti i dati</small></span></button>
          <button onClick={onInstall}><DownloadCloud /><span><strong>INSTALLA APP</strong><small>Home e modalità offline</small></span></button>
          <button onClick={onToggleSound}>
            {soundEnabled ? <Volume2 /> : <VolumeX />}
            <span><strong>SUONI {soundEnabled ? "ATTIVI" : "DISATTIVI"}</strong><small>Effetti MP3</small></span>
          </button>
        </div>

        <footer className="lobby-footer"><ShieldCheck /> Nessun login · salvataggio locale</footer>
      </div>
    </section>
  );
}

export default function App() {
  const [game, setGame] = useState<GameState>(
    savedGame ?? createGame(defaultDrafts, defaultConfig, "individual"),
  );
  const [past, setPast] = useState<GameState[]>(() => (savedGame ? loadUndoHistory() : []));
  const [preferences, setPreferences] = useState<AppPreferences>(() => loadPreferences());
  const [hasStarted, setHasStarted] = useState(savedGame?.status === "playing");
  const [lobbyOpen, setLobbyOpen] = useState(true);
  const [setupOpen, setSetupOpen] = useState(false);
  const [statsOpen, setStatsOpen] = useState(false);
  const [heatmapOpen, setHeatmapOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [tournamentOpen, setTournamentOpen] = useState(false);
  const [tournamentLink, setTournamentLink] = useState<ActiveTournamentLink | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [installPrompt, setInstallPrompt] = useState<Event | null>(null);
  const [updateReady, setUpdateReady] = useState<ServiceWorkerRegistration | null>(null);
  const importRef = useRef<HTMLInputElement>(null);

  useEffect(() => { if (hasStarted) saveGame(game); }, [game, hasStarted]);
  useEffect(() => { if (hasStarted) saveUndoHistory(past); }, [past, hasStarted]);
  useEffect(() => savePreferences(preferences), [preferences]);
  useEffect(() => preloadSounds(), []);
  useEffect(() => { void migrateLegacyHistory(); }, []);
  useEffect(() => {
    const syncViewport = () => {
      const height = window.visualViewport?.height ?? window.innerHeight;
      document.documentElement.style.setProperty("--app-height", `${height}px`);
    };
    syncViewport();
    window.addEventListener("resize", syncViewport);
    window.addEventListener("orientationchange", syncViewport);
    window.visualViewport?.addEventListener("resize", syncViewport);
    return () => {
      window.removeEventListener("resize", syncViewport);
      window.removeEventListener("orientationchange", syncViewport);
      window.visualViewport?.removeEventListener("resize", syncViewport);
    };
  }, []);
  useEffect(() => {
    const captureInstall = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event);
    };
    const captureUpdate = (event: Event) => {
      setUpdateReady((event as CustomEvent<ServiceWorkerRegistration>).detail);
    };
    window.addEventListener("beforeinstallprompt", captureInstall);
    window.addEventListener("bullseye:update-ready", captureUpdate);
    return () => {
      window.removeEventListener("beforeinstallprompt", captureInstall);
      window.removeEventListener("bullseye:update-ready", captureUpdate);
    };
  }, []);
  useEffect(() => {
    let sentinel: { release: () => Promise<void>; released?: boolean } | null = null;
    const requestWakeLock = async () => {
      if (lobbyOpen || game.status !== "playing" || document.visibilityState !== "visible") return;
      try {
        sentinel = await (navigator as Navigator & {
          wakeLock?: { request: (type: "screen") => Promise<{ release: () => Promise<void>; released?: boolean }> };
        }).wakeLock?.request("screen") ?? null;
      } catch {
        sentinel = null;
      }
    };
    void requestWakeLock();
    document.addEventListener("visibilitychange", requestWakeLock);
    return () => {
      document.removeEventListener("visibilitychange", requestWakeLock);
      if (sentinel && !sentinel.released) void sentinel.release();
    };
  }, [game.status, lobbyOpen]);
  useEffect(() => {
    if (game.status === "finished") {
      archiveGame(game);
      void (async () => {
        const archived = await archiveCompletedMatch(game, tournamentLink ? {
          tournamentId: tournamentLink.tournamentId,
          tournamentMatchId: tournamentLink.matchId,
        } : undefined);
        if (!tournamentLink || tournamentLink.recorded || tournamentLink.gameCreatedAt !== game.createdAt) return;
        const tournament = (await loadTournaments()).find((item) => item.id === tournamentLink.tournamentId);
        if (!tournament) return;
        const winnerIndex = game.teams.findIndex((item) => item.id === game.winnerTeamId);
        const next = recordTournamentResult(
          tournament,
          tournamentLink.matchId,
          tournamentLink.participantIds[Math.max(0, winnerIndex)],
          game.teams[0]?.legs ?? 0,
          game.teams[1]?.legs ?? 0,
          archived.id,
        );
        await saveTournament(next);
        setTournamentLink((current) => current ? { ...current, recorded: true } : current);
      })();
    }
  }, [game]);

  const active = activePlayer(game);
  const team = activeTeam(game);
  const matchDarts = useMemo(() => allThrows(game), [game]);

  const storeTransition = (next: GameState) => {
    if (next === game) return false;
    setPast((current) => [...current.slice(-99), game]);
    setGame(next);
    return true;
  };

  const scoreDart = (dart: DartInput) => {
    const next = throwDart(game, dart);
    if (!storeTransition(next)) return;
    playSound(scoreSoundCue(next.notice?.type), game.config.sound, preferences.soundVolume);
    if (next.notice?.type === "turn" || next.notice?.type === "bust") {
      playSound("nextplayer", game.config.sound, preferences.soundVolume, 150);
    }
    if (next.notice?.type === "bust" && navigator.vibrate) navigator.vibrate([80, 50, 130]);
  };

  const finishTurn = () => {
    const next = endTurn(game);
    if (!storeTransition(next)) return;
    playSound("nextplayer", game.config.sound, preferences.soundVolume);
  };

  const undo = () => {
    const previous = past.at(-1);
    if (!previous) return;
    setGame(previous);
    setPast((current) => current.slice(0, -1));
    playSound("back", game.config.sound, preferences.soundVolume);
  };

  const startGame = (
    drafts: TeamDraft[],
    mode: MatchMode,
    config: GameConfig,
    nextPreferences = preferences,
  ) => {
    setGame(createGame(drafts, config, mode));
    setPreferences(nextPreferences);
    setPast([]);
    setHasStarted(true);
    setLobbyOpen(false);
    setSetupOpen(false);
    setMenuOpen(false);
  };

  const importGame = async (file?: File) => {
    if (!file) return;
    try {
      const value = JSON.parse(await file.text());
      const parsed = migrateGame(value) ?? await importLocalBackup(value);
      if (!parsed) throw new Error();
      setPast((current) => [...current, game]);
      setGame(parsed);
      setHasStarted(parsed.status === "playing");
      setLobbyOpen(parsed.status !== "playing");
      setSetupOpen(false);
      setMenuOpen(false);
    } catch {
      window.alert("File partita non valido.");
    }
  };

  const installApp = async () => {
    if (installPrompt) {
      await (installPrompt as Event & { prompt: () => Promise<void> }).prompt();
      setInstallPrompt(null);
    } else {
      window.alert("Su iPhone: apri Condividi in Safari e scegli “Aggiungi alla schermata Home”.");
    }
  };

  const launchTournamentMatch = (tournament: Tournament, match: TournamentMatch) => {
    const first = tournament.participants.find((item) => item.id === match.participantAId);
    const second = tournament.participants.find((item) => item.id === match.participantBId);
    if (!first || !second) return;
    const nextGame = createGame(
      [
        { name: first.name, players: first.players },
        { name: second.name, players: second.players },
      ],
      tournament.config,
      tournament.mode,
    );
    setGame(nextGame);
    setPast([]);
    setHasStarted(true);
    setLobbyOpen(false);
    setSetupOpen(false);
    setTournamentOpen(false);
    setMenuOpen(false);
    setTournamentLink({
      tournamentId: tournament.id,
      matchId: match.id,
      participantIds: [first.id, second.id],
      gameCreatedAt: nextGame.createdAt,
    });
  };

  const requestNewGame = () => {
    if (
      hasStarted &&
      game.status === "playing" &&
      !window.confirm("C’è una partita in corso. Vuoi sostituirla con una nuova partita?")
    ) return;
    setSetupOpen(true);
    setMenuOpen(false);
  };

  return (
    <main className="app-shell layout-six">
      <header className="topbar">
        <button className="menu-trigger" onClick={() => setMenuOpen(!menuOpen)} aria-label="Apri menu">
          <MoreVertical />
        </button>
        <div className="brand-mark"><Target /></div>
        <div className="brand-copy"><span className="eyebrow">DARTS SCOREKEEPER</span><h1>BULLSEYE</h1></div>
        <div className="match-status">
          <span><strong>R{game.round}</strong><small>ROUND</small></span><i />
          <span><strong>L{game.leg}</strong><small>LEG</small></span><i />
          <span><strong>{game.config.startScore}</strong><small>{game.config.doubleOut ? "DOUBLE OUT" : "OPEN OUT"}</small></span>
        </div>
        <div className="header-actions">
          <button onClick={undo} disabled={!past.length} aria-label="Annulla"><Undo2 /></button>
          <button onClick={() => setStatsOpen(true)} aria-label="Statistiche"><BarChart3 /></button>
          <button
            className="heatmap-button"
            onClick={() => setHeatmapOpen(true)}
            aria-label="Apri heatmap 3D"
          >
            <Crosshair /><span>HEATMAP 3D</span>
          </button>
        </div>
      </header>

      {menuOpen && (
        <div className="quick-menu">
          <button onClick={() => { setLobbyOpen(true); setMenuOpen(false); }}><Home /> Lobby</button>
          <button onClick={requestNewGame}><Plus /> Nuova partita</button>
          <button onClick={() => { setStatsOpen(true); setMenuOpen(false); }}><BarChart3 /> Analisi match</button>
          <button onClick={() => { setHistoryOpen(true); setMenuOpen(false); }}><History /> Storico</button>
          <button onClick={() => { setTournamentOpen(true); setMenuOpen(false); }}><Trophy /> Tornei</button>
          <button onClick={() => exportGame(game)}><Download /> Esporta JSON</button>
          <button onClick={() => importRef.current?.click()}><Upload /> Importa partita</button>
          <button onClick={() => void exportLocalBackup(game)}><Download /> Backup completo</button>
          <button onClick={() => void installApp()}><DownloadCloud /> Installa app</button>
          <button
            onClick={() =>
              setGame((current) => ({
                ...current,
                config: { ...current.config, sound: !current.config.sound },
              }))
            }
          >
            {game.config.sound ? <Volume2 /> : <VolumeX />} Suoni {game.config.sound ? "attivi" : "disattivi"}
          </button>
        </div>
      )}
      <input
        ref={importRef}
        hidden
        type="file"
        accept="application/json"
        onChange={(event) => void importGame(event.target.files?.[0])}
      />
      {updateReady && (
        <div className="update-banner" role="status">
          <span>Nuova versione disponibile</span>
          <button onClick={() => {
            navigator.serviceWorker.addEventListener("controllerchange", () => window.location.reload(), { once: true });
            updateReady.waiting?.postMessage("SKIP_WAITING");
          }}>AGGIORNA</button>
          <button onClick={() => setUpdateReady(null)} aria-label="Chiudi aggiornamento"><X /></button>
        </div>
      )}

      {lobbyOpen && (
        <Lobby
          game={game}
          canResume={hasStarted && game.status === "playing"}
          soundEnabled={game.config.sound}
          onResume={() => setLobbyOpen(false)}
          onNew={requestNewGame}
          onTournaments={() => setTournamentOpen(true)}
          onHistory={() => setHistoryOpen(true)}
          onImport={() => importRef.current?.click()}
          onBackup={() => void exportLocalBackup(game)}
          onInstall={() => void installApp()}
          onToggleSound={() => setGame((current) => ({
            ...current,
            config: { ...current.config, sound: !current.config.sound },
          }))}
        />
      )}

      <PlayerRail game={game} />

      <section className="throw-panel">
        <div className="turn-heading">
          <div>
            <span className="live-dot" /> IN PEDANA
            <strong>{active.name}</strong>
            {game.mode === "teams" && <em>{team.name}</em>}
            <b className="landscape-active-score">{team.score}</b>
          </div>
          <div className="turn-darts">
            {[0, 1, 2].map((index) => (
              <span key={index} className={index < game.currentTurn.length ? "thrown" : ""}>
                {game.currentTurn[index]?.label ?? "➤"}
              </span>
            ))}
          </div>
          <div className="turn-total">
            <small>TURNO</small>
            <strong>{game.currentTurn.reduce((sum, dart) => sum + dart.value, 0)}</strong>
          </div>
        </div>

        <div className="special-scores">
          <button onClick={() => scoreDart({ segment: 0, multiplier: 0, label: "MISS" })}>
            <Target /><span>MISS</span><small>0 PUNTI</small>
          </button>
          <button onClick={() => scoreDart({ segment: 25, multiplier: 1, label: "BULL 25" })}>
            <span className="bull-icon">●</span><span>OUTER BULL</span><strong>25</strong>
          </button>
          <button className="bull-50" onClick={() => scoreDart({ segment: 25, multiplier: 2, label: "BULL 50" })}>
            <span className="bull-icon">◉</span><span>BULLSEYE</span><strong>50</strong>
          </button>
        </div>

        <div className="scoreboard-scroll">
          <ScoreSection title="SINGLE" multiplier={1} onScore={scoreDart} />
          <ScoreSection title="DOUBLE" multiplier={2} onScore={scoreDart} />
          <ScoreSection title="TRIPLE" multiplier={3} onScore={scoreDart} />
        </div>

        <div className="bottom-actions">
          <button className="undo-large" onClick={undo} disabled={!past.length}>
            <Undo2 /><span>ANNULLA</span>
          </button>
          <button className="next-player" disabled={!game.currentTurn.length} onClick={finishTurn}>
            <span>TERMINA TURNO</span><strong>PROSSIMO PLAYER</strong><i>›</i>
          </button>
        </div>
      </section>

      <footer className="app-footer">
        <span><ShieldCheck /> Salvataggio locale attivo</span>
        <span>{matchDarts.length} freccette registrate</span>
        <button onClick={requestNewGame}><Settings /> Setup match</button>
      </footer>

      {game.notice && (
        <div key={game.notice.id} className={`game-toast ${game.notice.type}`}>
          <strong>{game.notice.title}</strong><span>{game.notice.detail}</span>
        </div>
      )}
      {setupOpen && (
        <SetupModal
          initial={game}
          preferences={preferences}
          onStart={startGame}
          onClose={() => setSetupOpen(false)}
        />
      )}
      {statsOpen && <StatsModal game={game} onClose={() => setStatsOpen(false)} />}
      {historyOpen && <HistoryModal onClose={() => setHistoryOpen(false)} />}
      {tournamentOpen && <TournamentModal onClose={() => setTournamentOpen(false)} onLaunch={launchTournamentMatch} />}
      {heatmapOpen && (
        <Suspense fallback={<div className="modal-backdrop"><div className="loading-panel">Caricamento bersaglio 3D…</div></div>}>
          <Heatmap3D
            darts={matchDarts}
            players={game.players}
            teams={game.teams}
            mode={game.mode}
            onClose={() => setHeatmapOpen(false)}
          />
        </Suspense>
      )}
      {game.status === "finished" && !lobbyOpen && !setupOpen && !tournamentOpen && (
        <WinnerModal
          game={game}
          onRematch={() => {
            setGame(rematch(game));
            setPast([]);
            setHasStarted(true);
            setLobbyOpen(false);
          }}
          onNew={requestNewGame}
          onTournament={tournamentLink ? () => setTournamentOpen(true) : undefined}
        />
      )}
    </main>
  );
}
